'use client'

import Image from 'next/image'
import { motion, useMotionTemplate, useMotionValueEvent, useScroll, useTransform } from 'framer-motion'

// next/image with framer MotionValue support (style.objectPosition driven per-frame, no re-renders).
const MotionImage = motion.create(Image)
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/utils/styles/cn'
import { SuperscaleMark } from '@/features/landing-page/v3/superscale-mark'
import { VisionCtaButton } from '@/features/landing-page/v3/sections/vision-cta-button'

/**
 * lp-v3 "statement" section — a scroll-pinned photo card (a creative-studio scene) with a vertical
 * column of value-prop lines over it. Once the card reaches the top it PINS: the text stack and the
 * photo pan scroll continuously with the page (no stepped jumps). Exactly one line is highlighted
 * (bright + clover) once it settles at the screen centre; neighbours stay dim and readable while
 * they travel through. At the bottom it unpins full-bleed, then shrinks back to a card.
 *
 * All copy ships in the static HTML (SEO). Under reduced-motion / no-JS the section lays out in
 * normal flow with every line fully legible (no pin, no parallax). `!` beats the `.ds2` typography.
 */

const STATEMENT_LINES = [
    'Elevate your workflow with marketing agents',
    'You prompt what you want to get done, and your agents execute the work for you',
    'All it needs is your brand, product and business objective.',
]

// expand → pin (continuous text + photo scrub) → hold last line full-bleed → shrink
const EXPAND_END = 0.1
const PIN_START = 0.14
const PIN_END = 0.82
const SHRINK_START = 0.9

// Vertical spacing between row centres, in vh. Match the scroll-driven stack transform unit.
const LINE_STEP = 34

// How close a line must be to centre (in LINE_STEP units) before its highlight snaps on. Keeps the
// highlight stable while the stack is mid-travel between rows.
const HIGHLIGHT_SNAP = 0.18

// Room for a long continuous scrub across all lines (~1+ viewport of scroll per step).
const SECTION_VH = 380

export function LpStatementSection() {
    const wrapperRef = useRef<HTMLElement>(null)
    const cardRef = useRef<HTMLDivElement>(null)
    const [mounted, setMounted] = useState(false)
    const [reduce, setReduce] = useState(false)
    const [isDesktop, setIsDesktop] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    // Hysteresis for highlight: only update state when a line settles near centre (see HIGHLIGHT_SNAP).
    const activeIndexRef = useRef(0)

    useEffect(() => {
        setMounted(true)
        setReduce(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
        setIsDesktop(window.matchMedia('(min-width: 768px)').matches)
    }, [])

    const pinActive = mounted && !reduce
    const pinActiveRef = useRef(pinActive)
    pinActiveRef.current = pinActive
    const n = STATEMENT_LINES.length
    const lastIndex = n - 1

    // IMMERSIVE NAV: while the dark photo card overlaps the nav strip (top ~6% of the viewport),
    // tag <html> so the nav renders transparent with cream content (see globals.css
    // `.lp-nav-inverted`). Client-only side effect — SSR HTML untouched.
    useEffect(() => {
        const card = cardRef.current
        if (!card) return
        const observer = new IntersectionObserver(
            ([entry]) => document.documentElement.classList.toggle('lp-nav-inverted', entry.isIntersecting),
            { rootMargin: '0px 0px -94% 0px' }
        )
        observer.observe(card)
        return () => {
            observer.disconnect()
            document.documentElement.classList.remove('lp-nav-inverted')
        }
    }, [])

    // framer MotionValues — no re-renders per frame. Photo pan + text stack share the pin window so
    // they scrub together.
    const { scrollYProgress } = useScroll({ target: wrapperRef, offset: ['start start', 'end end'] })

    // Photo lightly zooms for the whole section; object-position pan is locked to the same pin
    // window as the text so both feel like one continuous scroll.
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.28])
    const imgObjectY = useTransform(scrollYProgress, [PIN_START, PIN_END], [0, 100], { clamp: true })
    const imgObjectPosition = useMotionTemplate`50% ${imgObjectY}%`

    // FULL-SCREEN EXPANSION via clip-path (resting gutter → full bleed → gutter).
    const gutter = isDesktop ? 30 : 16
    const cardInset = useTransform(scrollYProgress, [0, EXPAND_END, SHRINK_START, 1], [gutter, 0, 0, gutter], {
        clamp: true,
    })
    const cardRadius = useTransform(scrollYProgress, [0, EXPAND_END, SHRINK_START, 1], [24, 0, 0, 24], {
        clamp: true,
    })
    const cardClipPath = useMotionTemplate`inset(${cardInset}px round ${cardRadius}px)`

    // Continuous linear stack scrub over the pin window: hangs briefly on line 0 at PIN_START and on
    // the last line at PIN_END (so expand/shrink don't clip mid-travel); everything in between is a
    // smooth 0 → lastIndex glide — no plateaus, no discrete jumps.
    const pinSpan = PIN_END - PIN_START
    const stackOffset = useTransform(
        scrollYProgress,
        [0, PIN_START, PIN_START + pinSpan * 0.05, PIN_END - pinSpan * 0.08, PIN_END, 1],
        [0, 0, 0, lastIndex, lastIndex, lastIndex],
        { clamp: true }
    )
    const stackYVh = useTransform(stackOffset, (offset) => -offset * LINE_STEP)
    const stackTransform = useMotionTemplate`translateY(${stackYVh}vh)`

    useMotionValueEvent(stackOffset, 'change', (offset) => {
        if (!pinActiveRef.current) return
        const nearest = Math.min(lastIndex, Math.max(0, Math.round(offset)))
        // Only snap when that line is close enough to centre that it reads "in place".
        if (Math.abs(offset - nearest) > HIGHLIGHT_SNAP) return
        if (nearest === activeIndexRef.current) return
        activeIndexRef.current = nearest
        setActiveIndex(nearest)
    })
    useEffect(() => {
        if (!pinActive) return
        const offset = stackOffset.get()
        const nearest = Math.min(lastIndex, Math.max(0, Math.round(offset)))
        if (Math.abs(offset - nearest) > HIGHLIGHT_SNAP) return
        activeIndexRef.current = nearest
        setActiveIndex(nearest)
    }, [pinActive, lastIndex, stackOffset])

    const isActive = (index: number) => (pinActive ? index === activeIndex : true)
    const showClover = (index: number) => (pinActive ? index === activeIndex : index === 0)

    return (
        <section
            ref={wrapperRef}
            className="pb-[60px] md:pb-[100px]"
            style={pinActive ? { height: `${SECTION_VH}vh` } : undefined}
        >
            <div className={cn(pinActive && 'sticky top-0')}>
                <motion.div
                    ref={cardRef}
                    className="relative h-[100svh] w-full overflow-hidden bg-[#131210]"
                    style={pinActive ? { clipPath: cardClipPath, willChange: 'clip-path' } : undefined}
                >
                    <motion.div
                        className="absolute inset-0 origin-top will-change-transform"
                        style={pinActive ? { scale: imgScale } : undefined}
                    >
                        <MotionImage
                            src="/assets/landing-v3/statement/studio.jpg"
                            alt="A creator at their desk in an art-filled studio, working with Superscale"
                            fill
                            sizes="(max-width: 768px) 100vw, 960px"
                            className="object-cover object-top"
                            style={pinActive ? { objectPosition: imgObjectPosition } : undefined}
                        />
                    </motion.div>

                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-black/25" />
                    <div className="pointer-events-none absolute inset-0 md:bg-gradient-to-l md:from-black/30 md:via-transparent md:to-transparent" />

                    {/* Mobile: centre the copy column so clover+text sit as a unit (and with padding
                        ≥ the residual clip-path gutter so the mark never gets sliced). Desktop: right
                        column as before. */}
                    <div className="absolute inset-0 flex flex-col px-8 py-5 md:left-auto md:block md:w-auto md:px-10 md:py-10 lg:p-[60px]">
                        {pinActive ? (
                            <div className="relative mx-auto h-full w-full max-w-[280px] md:mx-0 md:max-w-none md:w-[334px]">
                                {/* Scroll-linked Y — continuous, never discrete animate() jumps. */}
                                <motion.div
                                    className="absolute inset-x-0 top-1/2 will-change-transform"
                                    style={{ transform: stackTransform }}
                                >
                                    {STATEMENT_LINES.map((line, index) => (
                                        <div
                                            key={line}
                                            className="absolute inset-x-0 -translate-y-1/2"
                                            style={{ top: `${index * LINE_STEP}vh` }}
                                        >
                                            <StatementRow
                                                text={line}
                                                active={isActive(index)}
                                                clover={showClover(index)}
                                            />
                                            {index === lastIndex && (
                                                <div
                                                    className={cn(
                                                        'mt-5 flex justify-center pl-0 transition-opacity duration-300 ease-out md:justify-start md:pl-[104px]',
                                                        isActive(index) ? 'opacity-100' : 'opacity-35'
                                                    )}
                                                >
                                                    <VisionCtaButton />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </motion.div>
                            </div>
                        ) : (
                            <div className="mx-auto flex h-full w-full max-w-[280px] flex-col justify-center gap-10 md:mx-0 md:max-w-none md:gap-14">
                                <StatementRow text={STATEMENT_LINES[0]} active clover={showClover(0)} />
                                <StatementRow text={STATEMENT_LINES[1]} active clover={showClover(1)} />
                                <div className="flex flex-col gap-5">
                                    <StatementRow text={STATEMENT_LINES[2]} active clover={showClover(2)} />
                                    <div className="flex justify-center pl-0 md:justify-start md:pl-[104px]">
                                        <VisionCtaButton />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </section>
    )
}

function StatementRow({ text, active, clover }: { text: string; active: boolean; clover: boolean }) {
    return (
        // Mobile: clover+text as a centred unit so neither is clipped by the left edge / residual
        // clip-path gutter. Desktop: left-aligned row inside the right column.
        <div className="flex w-full min-w-0 items-start justify-center md:justify-start">
            <div className="flex min-w-0 max-w-full items-start">
                <SuperscaleMark
                    className={cn(
                        'mt-[-1px] size-5 shrink-0 text-[#FDFBF8] transition-opacity duration-300 ease-out md:mt-px md:size-6',
                        'mr-3 md:mr-20',
                        clover ? 'opacity-100' : 'opacity-0'
                    )}
                />
                <p
                    className={cn(
                        'min-w-0 max-w-[min(200px,calc(100vw-7rem))] font-[family-name:var(--font-landing-body)]! text-[16px]! leading-[1.2]! tracking-[-0.01em]! text-[#FDFBF8] transition-opacity duration-300 ease-out md:w-[230px] md:max-w-[230px] md:text-[24px]! md:leading-[1.1]!',
                        active ? 'opacity-100' : 'opacity-35'
                    )}
                >
                    {text}
                </p>
            </div>
        </div>
    )
}
